import { useQuery } from '@tanstack/react-query';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { ApiError } from 'shared/errors/ApiError';

export const SUBSTITUTION_RULES_QUERY_KEY = 'fbmSubstitutionRules';

export const substitutionRulesQueryKeys = {
    all: [SUBSTITUTION_RULES_QUERY_KEY],
    list: (fbmAccountName, facilityId) => [SUBSTITUTION_RULES_QUERY_KEY, fbmAccountName, facilityId],
};

/**
 * Fetches substitution rules for an FBM account.
 *
 * @param {Object} options
 * @param {string} [options.fbmAccountName] - FBM account name (from user context)
 * @param {boolean} [options.enabled=true] - Whether the query should run
 */
export function useSubstitutionRulesQuery({ enabled = true, facilityId, fbmAccountName } = {}) {
    return useQuery({
        enabled: enabled && !!fbmAccountName,
        queryFn: async () => {
            const response = await fbmSubstitutionRulesV1Api.getRules(fbmAccountName, facilityId);
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch substitution rules',
                    response?.statusCode ?? 400,
                    response?.data,
                );
            }
            return response.data?.rows ?? [];
        },
        queryKey: substitutionRulesQueryKeys.list(fbmAccountName, facilityId),
        retry: (failureCount, err) => {
            if (err instanceof ApiError && (err.isServerError || err.isAuthError || err.isClientError)) {
                return false;
            }
            return failureCount < 2;
        },
        staleTime: 0,
    });
}
